/**
 * Cohort Retention Heat Map
 * Visual heat map showing weekly cohort retention with Day 1/7/30/90 metrics
 * Solis Games - Phase 2.2 Implementation
 */

import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const CohortRetentionHeatMap = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading cohort retention data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.heatMapData || data.heatMapData.length === 0) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Cohort Data Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Cohort retention data will appear once you have players returning to your game after their first session.
            This typically takes a few days to generate meaningful insights.
          </p>
        </div>
      </div>
    );
  }

  const getHeatColor = (value) => {
    if (value >= 70) return 'bg-green-500';
    if (value >= 50) return 'bg-yellow-500';
    if (value >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return { name: 'TrendingUp', color: 'text-green-500' };
    if (trend === 'declining') return { name: 'TrendingDown', color: 'text-red-500' };
    return { name: 'Minus', color: 'text-muted-foreground' };
  };

  const trendIcon = getTrendIcon(data.summary?.trend);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Calendar" size={24} className="text-primary" />
              Cohort Retention Analysis
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Weekly cohorts showing Day 1, 7, 30, and 90 retention rates
            </p>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-500/10">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              Excellent (70%+)
            </span>
            <span className="flex items-center gap-1.5 px-2 py-1 rounded bg-yellow-500/10">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              Good (50-70%)
            </span>
            <span className="flex items-center gap-1.5 px-2 py-1 rounded bg-orange-500/10">
              <div className="w-3 h-3 bg-orange-500 rounded"></div>
              Fair (30-50%)
            </span>
            <span className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              Poor (&lt;30%)
            </span>
          </div>
        </div>
      </div>

      {/* Heat Map Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-muted/30">
            <tr className="border-b border-border">
              <th className="text-left p-4 text-sm font-medium text-foreground">Cohort Week</th>
              <th className="text-center p-4 text-sm font-medium text-foreground">Size</th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                <div>Day 1</div>
                <div className="text-xs text-muted-foreground font-normal">24h Return</div>
              </th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                <div>Day 7</div>
                <div className="text-xs text-muted-foreground font-normal">Week Return</div>
              </th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                <div>Day 30</div>
                <div className="text-xs text-muted-foreground font-normal">Month Return</div>
              </th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                <div>Day 90</div>
                <div className="text-xs text-muted-foreground font-normal">Quarter Return</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.heatMapData.map((cohort, idx) => (
              <tr key={idx} className="border-b border-border hover:bg-muted/20 transition-colors">
                <td className="p-4 text-sm font-medium text-foreground whitespace-nowrap">
                  {cohort.cohort}
                </td>
                <td className="p-4 text-center">
                  <span className="text-sm text-muted-foreground font-mono">
                    {cohort.cohortSize.toLocaleString()}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <div className={`inline-flex items-center justify-center px-4 py-2 rounded-md ${getHeatColor(cohort.day1)} text-white text-sm font-semibold min-w-[70px] shadow-sm`}>
                    {cohort.day1.toFixed(1)}%
                  </div>
                </td>
                <td className="p-4 text-center">
                  <div className={`inline-flex items-center justify-center px-4 py-2 rounded-md ${getHeatColor(cohort.day7)} text-white text-sm font-semibold min-w-[70px] shadow-sm`}>
                    {cohort.day7.toFixed(1)}%
                  </div>
                </td>
                <td className="p-4 text-center">
                  <div className={`inline-flex items-center justify-center px-4 py-2 rounded-md ${getHeatColor(cohort.day30)} text-white text-sm font-semibold min-w-[70px] shadow-sm`}>
                    {cohort.day30.toFixed(1)}%
                  </div>
                </td>
                <td className="p-4 text-center">
                  <div className={`inline-flex items-center justify-center px-4 py-2 rounded-md ${getHeatColor(cohort.day90)} text-white text-sm font-semibold min-w-[70px] shadow-sm`}>
                    {cohort.day90.toFixed(1)}%
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Trend Line Chart */}
      <div className="p-6 border-t border-border bg-muted/10">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="LineChart" size={18} />
          Retention Trends Over Time
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.heatMapData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="cohort"
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: 12 }}
              domain={[0, 100]}
              label={{ value: 'Retention %', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                padding: '12px'
              }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 'bold', marginBottom: '8px' }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="day1"
              stroke="#22c55e"
              name="Day 1"
              strokeWidth={3}
              dot={{ fill: '#22c55e', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="day7"
              stroke="#3b82f6"
              name="Day 7"
              strokeWidth={3}
              dot={{ fill: '#3b82f6', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="day30"
              stroke="#f59e0b"
              name="Day 30"
              strokeWidth={3}
              dot={{ fill: '#f59e0b', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="day90"
              stroke="#ef4444"
              name="Day 90"
              strokeWidth={3}
              dot={{ fill: '#ef4444', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="p-6 border-t border-border bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-card rounded-lg border border-border">
            <div className="text-3xl font-bold text-green-500 mb-1">{data.summary.avgDay1}%</div>
            <div className="text-xs text-muted-foreground">Avg Day 1 Retention</div>
            <div className="text-xs text-muted-foreground mt-1">Next Day Return</div>
          </div>
          <div className="text-center p-4 bg-card rounded-lg border border-border">
            <div className="text-3xl font-bold text-blue-500 mb-1">{data.summary.avgDay7}%</div>
            <div className="text-xs text-muted-foreground">Avg Day 7 Retention</div>
            <div className="text-xs text-muted-foreground mt-1">Week Engagement</div>
          </div>
          <div className="text-center p-4 bg-card rounded-lg border border-border">
            <div className="text-3xl font-bold text-orange-500 mb-1">{data.summary.avgDay30}%</div>
            <div className="text-xs text-muted-foreground">Avg Day 30 Retention</div>
            <div className="text-xs text-muted-foreground mt-1">Monthly Stickiness</div>
          </div>
          <div className="text-center p-4 bg-card rounded-lg border border-border">
            <div className="text-3xl font-bold text-red-500 mb-1">{data.summary.avgDay90}%</div>
            <div className="text-xs text-muted-foreground">Avg Day 90 Retention</div>
            <div className="text-xs text-muted-foreground mt-1">Long-term Fans</div>
          </div>
          <div className="text-center p-4 bg-card rounded-lg border border-border">
            <div className={`flex items-center justify-center gap-2 mb-1`}>
              <Icon name={trendIcon.name} size={28} className={trendIcon.color} />
              <span className="text-lg font-bold text-foreground capitalize">{data.summary.trend}</span>
            </div>
            <div className="text-xs text-muted-foreground">Retention Trend</div>
            <div className="text-xs text-muted-foreground mt-1">vs Previous Period</div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="p-6 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Lightbulb" size={18} className="text-yellow-500" />
          Retention Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {parseFloat(data.summary.avgDay1) < 40 && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-red-500">Low Day 1 Retention</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Most players don't return after first session. Focus on improving onboarding and first-time experience.
                  </div>
                </div>
              </div>
            </div>
          )}
          {parseFloat(data.summary.avgDay7) > 50 && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-start gap-2">
                <Icon name="CheckCircle" size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-green-500">Strong Week Retention</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Over 50% of players return after a week. Your game has strong short-term engagement!
                  </div>
                </div>
              </div>
            </div>
          )}
          {data.summary.trend === 'improving' && (
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-start gap-2">
                <Icon name="TrendingUp" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-blue-500">Improving Trend</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Recent cohorts show better retention. Your improvements are working!
                  </div>
                </div>
              </div>
            </div>
          )}
          {data.summary.trend === 'declining' && (
            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <div className="flex items-start gap-2">
                <Icon name="TrendingDown" size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-orange-500">Declining Trend</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Recent cohorts have worse retention. Review recent changes or investigate player feedback.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CohortRetentionHeatMap;
